import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

export default function TrustIndicators({ data, onRefresh }) {
  if (!data || !data.trust_indicators) {
    return null;
  }

  const { trust_indicators } = data;
  const {
    is_balanced,
    total_entries,
    total_debits,
    total_credits,
    imbalance,
    last_updated
  } = trust_indicators;

  // Materiality tiers: a sub-$1 rounding residue must not scream like a real break
  const absImbalance = Math.abs(parseFloat(imbalance || 0));
  const severity = (is_balanced || absImbalance < 1) ? 'ok' : (absImbalance < 1000 ? 'minor' : 'critical');

  const formatCurrency = (value) => {
    const num = parseFloat(value || 0);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(num);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        background: severity === 'ok'
          ? 'linear-gradient(135deg, #4F46E5 0%, var(--color-primary) 50%, var(--color-accent) 100%)'
          : severity === 'minor'
            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
            : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: 'white'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="600" gutterBottom>
            Books Status
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Last updated: {formatDate(last_updated)}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            icon={severity === 'ok' ? <CheckCircleIcon /> : <WarningIcon />}
            label={severity === 'ok'
              ? (is_balanced ? 'Balanced' : 'Balanced (within rounding)')
              : severity === 'minor' ? 'Minor Imbalance' : 'Out of Balance'}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
              color: 'white',
              fontWeight: '600',
              fontSize: '0.95rem'
            }}
          />
          {onRefresh && (
            <Tooltip title="Refresh data">
              <IconButton
                onClick={onRefresh}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)'
                  }
                }}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Total Entries */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <AccountBalanceIcon sx={{ fontSize: 20, opacity: 0.9 }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Entries
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="700">
              {formatNumber(total_entries)}
            </Typography>
          </Box>
        </Grid>

        {/* Total Debits */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <TrendingUpIcon sx={{ fontSize: 20, opacity: 0.9 }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Debits
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight="700">
              {formatCurrency(total_debits)}
            </Typography>
          </Box>
        </Grid>

        {/* Total Credits */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <TrendingUpIcon sx={{ fontSize: 20, opacity: 0.9, transform: 'rotate(180deg)' }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Credits
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight="700">
              {formatCurrency(total_credits)}
            </Typography>
          </Box>
        </Grid>

        {/* Imbalance */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2,
              backgroundColor: severity === 'ok'
                ? 'rgba(76, 175, 80, 0.25)'
                : 'rgba(244, 67, 54, 0.25)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)',
              border: severity === 'ok' ? '2px solid rgba(76, 175, 80, 0.5)' : '2px solid rgba(244, 67, 54, 0.5)'
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {severity === 'ok' ? (
                <CheckCircleIcon sx={{ fontSize: 20 }} />
              ) : (
                <WarningIcon sx={{ fontSize: 20 }} />
              )}
              <Typography variant="body2" fontWeight="600">
                Imbalance
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight="700">
              {formatCurrency(imbalance)}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Balance Verification Message */}
      {severity === 'ok' && !is_balanced && (
        <Box
          mt={2}
          p={2}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="body1" fontWeight="600">
              Books balanced within rounding tolerance
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Residual difference of {formatCurrency(absImbalance)} across {formatNumber(total_entries)} entries is sub-materiality (&lt;$1) rounding.
            </Typography>
          </Box>
        </Box>
      )}
      {is_balanced && (
        <Box
          mt={2}
          p={2}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="body1" fontWeight="600">
              All journal entries are balanced
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Double-entry bookkeeping verified: Total debits equal total credits across all {formatNumber(total_entries)} entries
            </Typography>
          </Box>
        </Box>
      )}

      {severity !== 'ok' && (
        <Box
          mt={2}
          p={2}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <WarningIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="body1" fontWeight="600">
              {severity === 'minor' ? 'Minor imbalance detected' : 'Books are out of balance'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              There is a {formatCurrency(absImbalance)} discrepancy between debits and credits.
              {severity === 'minor' ? ' Worth investigating during the next close.' : ' This requires immediate attention.'}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
